import React, { useState, useEffect, useRef } from 'react';
import genericClusteringService from '../../services/genericClusteringService';
import { useAuth } from '../../contexts/AuthContext';
import GroupTimeSeriesChart from '../GroupTimeSeriesChart';
import ComparisonChart from '../ComparisonChart';
import ReportSummary from '../ReportSummary';
import { Folder } from 'lucide-react';

const CreativeBreakdown = ({ dashboardData, filters, onFilterChange }) => {
  const { getAccountId } = useAuth();
  const accountId = getAccountId();
  
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [urlClusters, setUrlClusters] = useState(null);
  const [progress, setProgress] = useState(0);
  const [topROASAds, setTopROASAds] = useState({});
  const [previewModal, setPreviewModal] = useState({ isOpen: false, assetUrl: '', mediaFormat: '', adName: '' });
  const [triggerAnalysis, setTriggerAnalysis] = useState(0); // Simple counter to trigger analysis
  const [expandedAdLists, setExpandedAdLists] = useState({}); // Track which ad lists are expanded
  const [adsModal, setAdsModal] = useState({ isOpen: false, cluster: null, ads: [], loading: false });
  const [viewMode, setViewMode] = useState('card'); // 'card', 'time-trend', 'comparison'
  
  // Use correlation filter from global Dashboard Configuration
  const correlationMetric = filters?.correlationMetric || 'roas';
  
  const progressIntervalRef = useRef(null);
  const lastRequestParams = useRef(null);
  const isInitialMount = useRef(true);
  const abortControllerRef = useRef(null);
  const debounceTimeoutRef = useRef(null);

  // Helper functions to get date range info from filters
  const getDateRangeInfo = () => {
    // Check for custom date range
    if (filters?.dateRangeType === 'custom' && filters?.startDate && filters?.endDate) {
      // Calculate days for custom range
      const start = new Date(filters.startDate + 'T00:00:00');
      const end = new Date(filters.endDate + 'T00:00:00');
      const calculatedDays = Math.ceil((end - start) / (1000 * 60 * 60 * 24)) + 1;
      
      return {
        type: 'custom',
        startDate: filters.startDate,
        endDate: filters.endDate,
        days: calculatedDays // Include calculated days for display purposes
      };
    }
    // Default to preset days
    const days = (filters?.dateRange && typeof filters.dateRange === 'number') ? filters.dateRange : 7;
    return {
      type: 'preset',
      days: days,
      startDate: null,
      endDate: null
    };
  };

  const getAdNameFilter = () => {
    if (filters?.adNameFilterKeywords && filters.adNameFilterKeywords.length > 0) {
      return {
        keywords: filters.adNameFilterKeywords.filter(k => k.trim()),
        logic: filters.adNameFilterLogic || 'AND',
        mode: filters.adNameFilterMode || 'include'
      };
    }
    return null;
  };

  // Trigger analysis when parameters change (same pattern as CopyThemeAnalysis)
  useEffect(() => {
    if (!filters) {
      console.log('⏳ CreativeBreakdown (Product) waiting for filters...');
      return;
    }
    console.log('🔄 CreativeBreakdown (Product) filters changed, reloading...', filters);
    if (!accountId) return;
    
    const dateRangeInfo = getDateRangeInfo();
    const adNameFilter = getAdNameFilter();
    
    // Create a unique key for the current parameters
    const currentParams = JSON.stringify({ dateRangeInfo, adNameFilter, accountId });
    
    // Only trigger if parameters actually changed or it's the initial mount
    if (isInitialMount.current || lastRequestParams.current !== currentParams) {
      // Use the trigger to cause the analysis effect to run
      setTriggerAnalysis(prev => prev + 1);
      lastRequestParams.current = currentParams;
      
      if (isInitialMount.current) {
        isInitialMount.current = false;
      }
    }
  }, [accountId, filters]);

  // Run analysis when triggered (same pattern as CopyThemeAnalysis)
  useEffect(() => {
    if (triggerAnalysis === 0) return; // Skip initial render
    
    // Clear any existing timeout
    if (debounceTimeoutRef.current) {
      clearTimeout(debounceTimeoutRef.current);
    }
    
    // Debounce the actual request
    debounceTimeoutRef.current = setTimeout(() => {
      const dateRangeInfo = getDateRangeInfo();
      const adNameFilter = getAdNameFilter();
      generateUrlAnalysis(true, dateRangeInfo, adNameFilter);
    }, 300);
    
    // Cleanup timeout
    return () => {
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }
    };
  }, [triggerAnalysis]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }
    };
  }, []);

  // Generate URL analysis (same pattern as CopyThemeAnalysis)
  const generateUrlAnalysis = async (forceRefresh = false, dateRangeInfo, adNameFilter) => {
    if (!accountId) return;
    
    console.log('📊 Product Analysis Starting:', {
      dateRangeInfo,
      adNameFilter,
      forceRefresh,
      accountId
    });
    
    try {
      setLoading(true);
      setError(null);
      setProgress(0);
      
      // Start progress bar (40 seconds)
      console.log('🔍 Starting progress bar interval');
      progressIntervalRef.current = setInterval(() => {
        setProgress(prev => {
          const newProgress = prev + (100 / 400); // 40 seconds = 400 * 100ms intervals
          console.log('🔍 Progress update:', prev, '->', newProgress);
          if (newProgress >= 100) {
            clearInterval(progressIntervalRef.current);
            console.log('🔍 Progress bar completed');
            return 100;
          }
          return newProgress;
        });
      }, 100);
      
      console.log('🔍 Starting URL clustering analysis...');
      
        // Build SQL date range based on type
        let sqlDateRange;
        if (dateRangeInfo.type === 'custom') {
          sqlDateRange = `adp.date >= '${dateRangeInfo.startDate}' AND adp.date <= '${dateRangeInfo.endDate}'`;
          console.log('🔄 CreativeBreakdown using CUSTOM date range:', dateRangeInfo.startDate, 'to', dateRangeInfo.endDate);
        } else {
          // Use timezone-aware CURRENT_DATE to match dashboard table and time series chart
          sqlDateRange = `(CURRENT_DATE AT TIME ZONE 'America/Los_Angeles')::date - INTERVAL '${dateRangeInfo.days} days'`;
          console.log('🔄 CreativeBreakdown using PRESET date range:', dateRangeInfo.days, 'days');
        }
        console.log('📊 SQL Date Range:', sqlDateRange);
        
        const result = await genericClusteringService.getComprehensiveAnalysis(
          accountId,
          sqlDateRange,
          adNameFilter,
          'url'
        );
      
      console.log('🔍 URL clustering result:', result);
      setUrlClusters(result);
      
      // Fetch top ROAS ads for each cluster
      if (result.clusters && result.clusters.length > 0) {
        await fetchTopROASAds(result.clusters);
      }
      
    } catch (err) {
      console.error('Error generating URL clusters:', err);
      setError(err.message);
    } finally {
      setLoading(false);
      clearInterval(progressIntervalRef.current);
      setProgress(100);
    }
  };

  // Fetch top ROAS ads for each cluster (same logic as CopyThemeAnalysis)
  const fetchTopROASAds = async (clusters) => {
    console.log('🔍 fetchTopROASAds called with clusters:', clusters.length);
    if (!clusters || clusters.length === 0) return;
    
    const adsByCluster = {};
    
    for (const cluster of clusters) {
      if (cluster.ads && cluster.ads.length > 0) {
        console.log('🔍 Processing cluster:', cluster.name, 'with', cluster.ads.length, 'ads');
        try {
          const topAds = await genericClusteringService.getTopAdsByMetric(cluster.ads, 3, correlationMetric, filters?.dateRange);
          console.log('🔍 API returned', topAds.length, 'ads for cluster');
          const clusterKey = cluster.name;
          adsByCluster[clusterKey] = topAds.map(ad => {
            console.log('🔍 All Ad Debug:', {
              adName: ad.ad_name,
              assetUrl: ad.asset_url,
              mediaFormat: ad.media_format,
              hasAssetUrl: !!ad.asset_url,
              assetUrlLength: ad.asset_url?.length || 0,
              assetUrlPreview: ad.asset_url?.substring(0, 50) + '...'
            });
            return {
              adName: ad.ad_name,
              roas: parseFloat(ad.roas || 0).toFixed(1),
              spend: parseFloat(ad.total_spend || 0),
              revenue: parseFloat(ad.total_revenue || 0),
              ctr: parseFloat(ad.ctr || 0),
              assetUrl: ad.asset_url,
              mediaFormat: ad.media_format,
              copyText: ad.headline || ad.primary_text || ad.audio_text_content || ad.text_overlay_content || ad.description
            };
          });
        } catch (error) {
          console.error('Error fetching top ROAS ads for cluster:', error);
          const clusterKey = cluster.name;
          adsByCluster[clusterKey] = [];
        }
      }
    }
    
    console.log('🔍 Final adsByCluster:', adsByCluster);
    setTopROASAds(adsByCluster);
  };

  if (loading) {
    console.log('🔍 Rendering loading state, progress:', progress);
    return (
      <div className="space-y-6">
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
          <div className="flex flex-col items-center justify-center">
            <div className="flex items-center mb-4">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mr-3"></div>
              <div className="text-blue-800">
                <div className="font-medium">🤖 AI Analysis in Progress...</div>
                <div className="text-sm">Analyzing product content and generating URL clusters</div>
              </div>
            </div>
            {/* Progress Bar */}
            <div className="w-full max-w-md mb-4">
              <div className="flex justify-between items-center mb-2">
                <span className="text-sm font-medium text-blue-800">Analysis Progress</span>
                <span className="text-sm text-blue-600">{Math.round(progress)}%</span>
              </div>
              <div className="w-full bg-blue-200 rounded-full h-3">
                <div 
                  className="bg-blue-600 h-3 rounded-full transition-all duration-300 ease-out"
                  style={{ width: `${progress}%` }}
                ></div>
              </div>
            </div>
            
            <div className="text-center text-blue-700">
              <div className="text-sm mb-2">Expected completion: 40 seconds</div>
              <div className="text-xs text-blue-600 mb-3">The AI is processing your ad product content to identify URL patterns and clusters.</div>
              <div className="text-xs text-blue-500">⚠️ Important: Please don't close this window or switch to other tabs during the analysis process.</div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="space-y-6">
        <div className="bg-red-50 border border-red-200 rounded-lg p-6">
          <div className="text-center">
            <h3 className="text-lg font-semibold text-red-800 mb-2">Error</h3>
            <p className="text-red-600">{error}</p>
          </div>
        </div>
      </div>
    );
  }

  if (!urlClusters || !urlClusters.clusters || urlClusters.clusters.length === 0) {
    return (
      <div className="space-y-6">
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="text-center">
            <h3 className="text-lg font-semibold text-gray-900 mb-2">No URL Clusters Found</h3>
            <p className="text-gray-600">No URL patterns were detected in your ad data.</p>
          </div>
        </div>
      </div>
    );
  }

  // Helper function to get top ROAS ads for a cluster
  const getTopROASAds = (cluster) => {
    return topROASAds[cluster.name] || [];
  };

  // Fetch all ads for a cluster - optimized to use the API endpoint directly
  const fetchAllAdsForCluster = async (cluster) => {
    if (!cluster.ads || cluster.ads.length === 0) return [];
    
    try {
      // Use the optimized API endpoint that filters by ad IDs server-side
      // Pass a high limit to get all ads in the cluster
      const allAds = await genericClusteringService.getTopAdsByMetric(
        cluster.ads, 
        cluster.ads.length, // Get all ads, not just top 3
        correlationMetric, 
        filters?.dateRange
      );
      
      return allAds.map(ad => ({
        adName: ad.ad_name,
        roas: parseFloat(ad.roas || 0).toFixed(1),
        spend: parseFloat(ad.total_spend || 0),
        revenue: parseFloat(ad.total_revenue || 0),
        ctr: parseFloat(ad.ctr || 0),
        assetUrl: ad.asset_url,
        mediaFormat: ad.media_format || (ad.asset_url?.includes('.mp4') || ad.asset_url?.includes('.mov') ? 'Video' : 'Image'),
        copyText: ad.headline || ad.primary_text || ad.audio_text_content || ad.text_overlay_content || ad.description
      }));
    } catch (error) {
      console.error('Error fetching all ads for cluster:', error);
      return [];
    }
  };

  const toggleAdListExpansion = (clusterName) => {
    setExpandedAdLists(prev => ({
      ...prev,
      [clusterName]: !prev[clusterName]
    }));
  };

  // Helper function to render thumbnail
  const renderThumbnail = (assetUrl, mediaFormat, adName, className = "w-16 h-16") => {
    if (!assetUrl) {
      return (
        <div className={`${className} bg-gray-100 rounded-lg flex items-center justify-center`}>
          <span className="text-xs text-gray-500 text-center">No Asset</span>
        </div>
      );
    }

    if (mediaFormat === 'Video') {
      return (
        <div 
          className={`${className} relative bg-gray-200 rounded-lg overflow-hidden cursor-pointer hover:scale-105 transition-transform`}
          onClick={() => setPreviewModal({ isOpen: true, assetUrl, mediaFormat, adName })}
          title={`Click to preview ${adName}`}
        >
          <video
            src={assetUrl}
            className="w-full h-full object-cover"
            preload="metadata"
            muted
            playsInline
            onError={(e) => {
              e.target.style.display = 'none';
              e.target.nextSibling.style.display = 'flex';
            }}
          />
          <div className="absolute inset-0 bg-black bg-opacity-50 flex items-center justify-center text-white text-2xl">
            ▶️
          </div>
          <div 
            className="absolute inset-0 bg-gray-200 flex items-center justify-center text-gray-400 text-xs hidden"
          >
            Click to View
          </div>
        </div>
      );
    }

    return (
      <img 
        src={assetUrl} 
        alt={adName}
        className={`${className} object-cover rounded border cursor-pointer hover:scale-105 transition-transform`}
        onClick={() => setPreviewModal({
          isOpen: true,
          assetUrl: assetUrl,
          mediaFormat: mediaFormat,
          adName: adName
        })}
      />
    );
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <div className="flex items-center justify-between mb-2">
          <div>
            <h2 className="text-xl font-semibold text-gray-900">📦 Product Breakdown</h2>
            <p className="text-sm text-gray-600 mt-1">AI product clustering based on URL patterns and landing page analysis</p>
          </div>
          
          {/* View Toggle */}
          {urlClusters?.clusters && urlClusters.clusters.length > 0 && (
            <div className="flex items-center space-x-2 bg-gray-100 rounded-lg p-1">
              <button
                onClick={() => setViewMode('card')}
                className={`px-5 py-2.5 rounded-lg text-sm font-semibold transition-all duration-200 ${
                  viewMode === 'card'
                    ? 'bg-gradient-to-r from-indigo-500 to-purple-600 text-white shadow-lg transform scale-105'
                    : 'text-gray-600 hover:text-gray-900 hover:bg-white/50'
                }`}
              >
                Card
              </button>
              <button
                onClick={() => setViewMode('time-trend')}
                className={`px-5 py-2.5 rounded-lg text-sm font-semibold transition-all duration-200 ${
                  viewMode === 'time-trend'
                    ? 'bg-gradient-to-r from-indigo-500 to-purple-600 text-white shadow-lg transform scale-105'
                    : 'text-gray-600 hover:text-gray-900 hover:bg-white/50'
                }`}
              >
                Time Trend
              </button>
              <button
                onClick={() => setViewMode('comparison')}
                className={`px-5 py-2.5 rounded-lg text-sm font-semibold transition-all duration-200 ${
                  viewMode === 'comparison'
                    ? 'bg-gradient-to-r from-indigo-500 to-purple-600 text-white shadow-lg transform scale-105'
                    : 'text-gray-600 hover:text-gray-900 hover:bg-white/50'
                }`}
              >
                Comparison
              </button>
            </div>
          )}
        </div>
      </div>

      {/* AI Summary Section - Right after title */}
      {urlClusters?.clusters && urlClusters.clusters.length > 0 && (
        <ReportSummary 
          reportType="creative-breakdown"
          reportData={urlClusters.clusters}
          filters={filters}
          accountId={getAccountId()}
        />
      )}

      {/* Data Coverage Summary */}
      <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
        <div className="text-sm text-gray-600">
          <strong>📊 Data Coverage:</strong> Analyzed {urlClusters?.totalSamples || 0} ads, 
          showing {urlClusters?.clusters?.reduce((sum, cluster) => sum + (cluster.ads?.length || 0), 0) || 0} ads in {urlClusters?.clusters?.length || 0} clusters
          {urlClusters?.totalSamples && urlClusters?.clusters?.reduce((sum, cluster) => sum + (cluster.ads?.length || 0), 0) < urlClusters.totalSamples && 
            ` (${Math.round((urlClusters.clusters.reduce((sum, cluster) => sum + (cluster.ads?.length || 0), 0) / urlClusters.totalSamples) * 100)}% coverage)`
          }
        </div>
      </div>

      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-lg font-semibold text-gray-900">Product Clusters</h3>
          <div className="text-sm text-gray-600 bg-blue-50 px-3 py-1 rounded">
            📊 Date Range: {(() => {
              const info = getDateRangeInfo();
              return info.type === 'custom' 
                ? `${info.startDate} to ${info.endDate}`
                : `Last ${info.days} days`;
            })()}
          </div>
        </div>
        
        {/* Summary Metrics */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <div className="text-center p-4 bg-blue-50 rounded-lg">
            <div className="text-2xl font-bold text-blue-600">
              {urlClusters.clusters?.length || 0}
            </div>
            <div className="text-sm text-blue-700">Product Clusters</div>
          </div>
          <div className="text-center p-4 bg-green-50 rounded-lg">
            <div className="text-2xl font-bold text-green-600">
              {urlClusters.clusters?.reduce((sum, cluster) => sum + (cluster.ads?.length || 0), 0) || 0}
            </div>
            <div className="text-sm text-green-700">Total Ads</div>
          </div>
          <div className="text-center p-4 bg-purple-50 rounded-lg">
            <div className="text-2xl font-bold text-purple-600">
              {urlClusters.clusters?.length > 0 ? 
                (urlClusters.clusters.reduce((sum, cluster) => sum + (parseFloat(cluster.performance?.avg_roas) || 0), 0) / urlClusters.clusters.length).toFixed(1) : '0'}x
            </div>
            <div className="text-sm text-purple-700">Avg ROAS</div>
          </div>
          <div className="text-center p-4 bg-orange-50 rounded-lg">
            <div className="text-2xl font-bold text-orange-600">
              {urlClusters.clusters?.length > 0 ? 
                (urlClusters.clusters.reduce((sum, cluster) => sum + (parseFloat(cluster.performance?.avg_ctr) || 0), 0) / urlClusters.clusters.length).toFixed(1) : '0'}%
            </div>
            <div className="text-sm text-orange-700">Avg CTR</div>
          </div>
        </div>

        {/* Comparison Chart View */}
        {viewMode === 'comparison' && urlClusters.clusters && urlClusters.clusters.length > 0 && (
          <div className="mt-6">
            <ComparisonChart
              title="Product Clusters Comparison"
              data={urlClusters.clusters.map(cluster => ({
                name: (cluster.name || 'Unknown').substring(0, 30),
                spend: parseFloat(cluster.performance?.total_spend || 0),
                roas: parseFloat(cluster.performance?.avg_roas || 0),
                ctr: parseFloat(cluster.performance?.avg_ctr || 0),
                revenue: parseFloat(cluster.performance?.total_revenue || 0)
              }))}
              xAxisKey="name"
              metrics={[
                { key: 'spend', label: 'Spend', color: '#8b5cf6', format: 'currency', yAxisId: 'left' },
                { key: 'roas', label: 'ROAS', color: '#ec4899', format: 'number', yAxisId: 'right' }
              ]}
              height={400}
              maxItems={15}
            />
          </div>
        )}
        
        {/* Card Grid View */}
        {viewMode === 'card' && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
          {urlClusters.clusters
            .sort((a, b) => {
              const aPerformance = a.performance || {};
              const bPerformance = b.performance || {};
              
              let aValue, bValue;
              
              if (correlationMetric === 'revenue') {
                // Use total revenue when revenue is the correlation metric
                aValue = parseFloat(aPerformance.total_revenue || 0);
                bValue = parseFloat(bPerformance.total_revenue || 0);
              } else if (correlationMetric === 'spend') {
                // Use total spend when spend is the correlation metric
                aValue = parseFloat(aPerformance.total_spend || 0);
                bValue = parseFloat(bPerformance.total_spend || 0);
              } else if (correlationMetric === 'ctr') {
                aValue = parseFloat(aPerformance.avg_ctr || 0);
                bValue = parseFloat(bPerformance.avg_ctr || 0);
              } else if (correlationMetric === 'cpa') {
                aValue = parseFloat(aPerformance.avg_cpa || 0);
                bValue = parseFloat(bPerformance.avg_cpa || 0);
              } else { // roas
                aValue = parseFloat(aPerformance.avg_roas || 0);
                bValue = parseFloat(bPerformance.avg_roas || 0);
              }
              
              // For CPA, lower is better (ascending). For others, higher is better (descending)
              return correlationMetric === 'cpa' ? aValue - bValue : bValue - aValue;
            })
            .map((cluster, index) => {
            const clusterName = cluster.name;
            // Get first ad for preview
            const topAds = getTopROASAds(cluster);
            const previewAd = topAds && topAds.length > 0 ? topAds[0] : null;
            
            const isTop3 = index < 3;
            
            return (
              <div key={index} className={`relative rounded-2xl overflow-hidden transition-all duration-300 transform hover:scale-[1.02] hover:shadow-2xl group ${
                isTop3
                  ? 'bg-gradient-to-br from-purple-50 via-white to-purple-50 border-2 border-purple-300 shadow-lg ring-2 ring-purple-200 ring-opacity-50'
                  : 'bg-white border border-gray-200 hover:border-gray-300 shadow-sm'
              }`}>
                {/* Rank Badge for Top 3 */}
                {isTop3 && (
                  <div className="absolute top-3 left-3 z-10 w-10 h-10 flex items-center justify-center rounded-full bg-gradient-to-br from-purple-500 to-pink-600 text-white text-base font-bold shadow-xl ring-2 ring-white">
                    {index + 1}
                  </div>
                )}
                
                {/* Media Preview */}
                <div className={`relative aspect-square overflow-hidden ${
                  isTop3 ? 'bg-gradient-to-br from-purple-100 to-purple-50' : 'bg-gray-100'
                }`}>
                  {isTop3 && (
                    <div className="absolute inset-0 z-0 opacity-20 bg-gradient-to-br from-purple-400 to-purple-300" />
                  )}
                  {previewAd && previewAd.assetUrl ? (
                    <div 
                      className="w-full h-full relative cursor-pointer"
                      onClick={(e) => {
                        e.stopPropagation();
                        setPreviewModal({ isOpen: true, assetUrl: previewAd.assetUrl, mediaFormat: previewAd.mediaFormat, adName: previewAd.adName });
                      }}
                      title="Click to preview"
                    >
                      {renderThumbnail(previewAd.assetUrl, previewAd.mediaFormat, previewAd.adName, "w-full h-full")}
                    </div>
                  ) : (
                    <div className="w-full h-full relative bg-gradient-to-br from-purple-400 to-purple-600 flex items-center justify-center">
                      <div className="text-white text-4xl">📦</div>
                      <div className="absolute top-2 right-2 bg-gradient-to-r from-purple-500 to-purple-600 text-white text-xs px-2.5 py-1 rounded-full font-semibold shadow-lg">
                        Product Cluster
                      </div>
                    </div>
                  )}
                </div>
                
                {/* Card Content */}
                <div className={`p-5 ${isTop3 ? 'bg-white/80 backdrop-blur-sm' : ''}`}>
                  {/* Cluster Name */}
                  <h4 className={`font-bold text-base mb-2 line-clamp-1 ${
                    isTop3 ? 'text-purple-900' : 'text-gray-900'
                  }`} title={cluster.name}>
                    {cluster.name || 'Unknown Cluster'}
                  </h4>
                  
                  {/* Description */}
                  {cluster.description && (
                    <p className={`text-xs mb-3 line-clamp-2 ${
                      isTop3 ? 'text-purple-700' : 'text-gray-600'
                    }`} title={cluster.description}>
                      {cluster.description}
                    </p>
                  )}
                  
                  {/* Metrics Tags */}
                  <div className="space-y-3">
                    {/* Primary Metrics Row */}
                    <div className="flex flex-wrap gap-2">
                      <div className={`inline-flex items-center px-3 py-1.5 rounded-lg text-xs font-bold shadow-sm transition-all ${
                        correlationMetric === 'roas' 
                          ? isTop3
                            ? 'bg-gradient-to-r from-purple-500 to-purple-600 text-white'
                            : 'bg-green-100 text-green-800 border-2 border-green-300'
                          : 'bg-gray-100 text-gray-700'
                      }`}>
                        <span className="mr-1.5">ROAS:</span> 
                        <span className="text-sm">{cluster.performance?.avg_roas || 'N/A'}x</span>
                      </div>
                      <div className={`inline-flex items-center px-3 py-1.5 rounded-lg text-xs font-bold shadow-sm transition-all ${
                        correlationMetric === 'spend' 
                          ? isTop3
                            ? 'bg-gradient-to-r from-purple-500 to-purple-600 text-white'
                            : 'bg-purple-100 text-purple-800 border-2 border-purple-300'
                          : 'bg-gray-100 text-gray-700'
                      }`}>
                        <span className="mr-1.5">Spend:</span> 
                        <span className="text-sm">${parseFloat(cluster.performance?.total_spend || 0).toFixed(0)}</span>
                      </div>
                    </div>
                    
                    {/* Secondary Metrics Row */}
                    <div className="flex flex-wrap gap-2">
                      <div className={`inline-flex items-center px-3 py-1.5 rounded-lg text-xs font-bold shadow-sm transition-all ${
                        correlationMetric === 'ctr' 
                          ? isTop3
                            ? 'bg-gradient-to-r from-purple-500 to-purple-600 text-white'
                            : 'bg-blue-100 text-blue-800 border-2 border-blue-300'
                          : 'bg-gray-100 text-gray-700'
                      }`}>
                        <span className="mr-1.5">CTR:</span> 
                        <span className="text-sm">{cluster.performance?.avg_ctr || 'N/A'}%</span>
                      </div>
                      {correlationMetric === 'cpa' && (
                        <div className={`inline-flex items-center px-3 py-1.5 rounded-lg text-xs font-bold shadow-sm ${
                          isTop3
                            ? 'bg-gradient-to-r from-orange-500 to-orange-600 text-white'
                            : 'bg-orange-100 text-orange-800 border-2 border-orange-300'
                        }`}>
                          <span className="mr-1.5">CPA:</span> 
                          <span className="text-sm">${parseFloat(cluster.performance?.avg_cpa || 0).toFixed(2)}</span>
                        </div>
                      )}
                      <div className={`inline-flex items-center px-3 py-1.5 rounded-lg text-xs font-bold shadow-sm ${
                        isTop3 ? 'bg-gradient-to-r from-purple-500 to-purple-600 text-white' : 'bg-gray-100 text-gray-700'
                      }`}>
                        <span className="mr-1.5">Ads:</span> 
                        <span className="text-sm">{cluster.ads?.length || 0}</span>
                      </div>
                    </div>
                    
                    {/* Revenue */}
                    <div className={`flex flex-wrap gap-2 text-xs font-semibold ${
                      isTop3 ? 'text-purple-700' : 'text-gray-600'
                    }`}>
                      <div className="px-2 py-1 bg-gray-50 rounded-lg">
                        <span className="mr-1">Revenue:</span> 
                        <span className="text-sm">${parseFloat(cluster.performance?.total_revenue || 0).toFixed(0)}</span>
                      </div>
                    </div>
                  </div>
                </div>
                
                {/* Folder Button - Bottom Right */}
                <div className="absolute bottom-4 right-4 opacity-0 group-hover:opacity-100 transition-all duration-300 transform group-hover:scale-110">
                  <button
                    onClick={async (e) => {
                      e.stopPropagation();
                      // Reset modal state first to avoid showing previous data
                      const topAds = getTopROASAds(cluster);
                      const currentCluster = cluster; // Capture current cluster
                      
                      // Show modal immediately with top ads
                      setAdsModal({
                        isOpen: true,
                        cluster: currentCluster,
                        ads: topAds || [],
                        loading: true
                      });
                      
                      // Fetch all ads in the background
                      try {
                        const allAds = await fetchAllAdsForCluster(currentCluster);
                        // Only update if modal is still open and for the same cluster
                        setAdsModal(prev => {
                          if (prev.isOpen && prev.cluster?.name === currentCluster.name) {
                            return {
                              ...prev,
                              ads: allAds,
                              loading: false
                            };
                          }
                          return prev;
                        });
                      } catch (error) {
                        console.error('Error loading all ads:', error);
                        setAdsModal(prev => {
                          if (prev.isOpen && prev.cluster?.name === currentCluster.name) {
                            return {
                              ...prev,
                              loading: false
                            };
                          }
                          return prev;
                        });
                      }
                    }}
                    className={`p-3 rounded-full shadow-xl pointer-events-auto transition-all duration-300 ${
                      isTop3
                        ? 'bg-gradient-to-br from-purple-500 to-purple-600 hover:from-purple-600 hover:to-purple-700 text-white'
                        : 'bg-gradient-to-br from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white'
                    }`}
                    title="View all ads in this cluster"
                  >
                    <Folder className="w-5 h-5" />
                  </button>
                </div>
              </div>
            );
          })}
          </div>
        )}
      </div>

      {/* Preview Modal */}
      {previewModal.isOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60]" onClick={() => setPreviewModal({ isOpen: false, assetUrl: '', mediaFormat: '', adName: '' })}>
          <div className="bg-white rounded-lg max-w-4xl max-h-[90vh] overflow-hidden flex flex-col" onClick={(e) => e.stopPropagation()}>
            {/* Header */}
            <div className="flex justify-between items-center p-4 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900 truncate pr-4" title={previewModal.adName}>
                {previewModal.adName}
              </h3>
              <button 
                onClick={() => setPreviewModal({ isOpen: false, assetUrl: '', mediaFormat: '', adName: '' })}
                className="text-gray-400 hover:text-gray-600 text-2xl flex-shrink-0"
                title="Close"
              >
                ×
              </button>
            </div>
            
            {/* Content */}
            <div className="p-4">
              {previewModal.mediaFormat === 'Video' ? (
                <video 
                  src={previewModal.assetUrl} 
                  controls 
                  className="w-full h-auto max-h-[70vh] rounded-lg shadow-lg"
                  autoPlay
                  preload="metadata"
                >
                  <source src={previewModal.assetUrl} type="video/mp4" />
                  Your browser does not support the video tag.
                </video>
              ) : (
                <img 
                  src={previewModal.assetUrl} 
                  alt={previewModal.adName}
                  className="w-full h-auto max-h-[70vh] object-contain rounded-lg shadow-lg"
                />
              )}
            </div>
            
            {/* Footer */}
            <div className="p-4 border-t border-gray-200 bg-gray-50">
              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-600">
                  {previewModal.mediaFormat === 'Video' ? '🎥 Video Asset' : '🖼️ Image Asset'}
                </span>
                <div className="flex space-x-3">
                  <button
                    onClick={() => {
                      navigator.clipboard.writeText(previewModal.assetUrl).then(() => {
                        // Show a temporary success message
                        const btn = document.activeElement;
                        const originalText = btn.textContent;
                        btn.textContent = '✓ Copied!';
                        setTimeout(() => {
                          btn.textContent = originalText;
                        }, 2000);
                      }).catch(err => {
                        console.error('Failed to copy URL:', err);
                        alert('Failed to copy URL. Please try again.');
                      });
                    }}
                    className="px-3 py-1 text-sm text-green-600 hover:text-green-800 border border-green-300 rounded hover:bg-green-50 transition-colors"
                    title="Copy asset URL to clipboard"
                  >
                    Copy URL
                  </button>
                  <button
                    onClick={() => setPreviewModal({ isOpen: false, assetUrl: '', mediaFormat: '', adName: '' })}
                    className="px-3 py-1 text-sm text-gray-600 hover:text-gray-800 border border-gray-300 rounded hover:bg-gray-100 transition-colors"
                  >
                    Close
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* All Ads Modal */}
      {adsModal.isOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4" onClick={() => setAdsModal({ isOpen: false, cluster: null, ads: [], loading: false })}>
          <div className="bg-white rounded-lg max-w-6xl w-full max-h-[90vh] overflow-hidden flex flex-col" onClick={(e) => e.stopPropagation()}>
            {/* Header */}
            <div className="flex justify-between items-center p-6 border-b border-gray-200">
              <div>
                <h3 className="text-xl font-semibold text-gray-900">
                  All Ads: {adsModal.cluster?.name || 'Unknown Cluster'}
                </h3>
                <p className="text-sm text-gray-600 mt-1">
                  {adsModal.ads.length} ads in this product cluster
                </p>
              </div>
              <button 
                onClick={() => setAdsModal({ isOpen: false, cluster: null, ads: [], loading: false })}
                className="text-gray-400 hover:text-gray-600 text-2xl"
              >
                ✕
              </button>
            </div>
            
            {/* Content - Grid of Ads */}
            <div className="flex-1 overflow-y-auto p-6">
              {adsModal.loading && adsModal.ads.length === 0 ? (
                <div className="flex items-center justify-center py-12">
                  <div className="text-center">
                    <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mb-4"></div>
                    <p className="text-gray-500">Loading ads...</p>
                  </div>
                </div>
              ) : adsModal.ads.length > 0 ? (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {adsModal.loading && (
                    <div className="col-span-full flex items-center justify-center py-4">
                      <div className="flex items-center space-x-2 text-sm text-gray-600">
                        <div className="inline-block animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
                        <span>Loading more ads...</span>
                      </div>
                    </div>
                  )}
                  {adsModal.ads.map((ad, index) => (
                    <div key={index} className="border border-gray-200 rounded-lg overflow-hidden hover:shadow-lg transition-all bg-white">
                      {/* Media Preview */}
                      {ad.assetUrl && (
                        <div 
                          className="relative bg-gray-100 aspect-square cursor-pointer"
                          onClick={() => setPreviewModal({ isOpen: true, assetUrl: ad.assetUrl, mediaFormat: ad.mediaFormat, adName: ad.adName })}
                          title="Click to preview"
                        >
                          {renderThumbnail(ad.assetUrl, ad.mediaFormat, ad.adName, "w-full h-full")}
                        </div>
                      )}
                      
                      {/* Ad Info */}
                      <div className="p-4">
                        <h4 className="font-semibold text-gray-900 text-sm mb-2 line-clamp-2" title={ad.adName}>
                          {ad.adName || 'Unknown Ad'}
                        </h4>
                        <div className="flex flex-wrap gap-2 text-xs">
                          <div className="inline-flex items-center px-2 py-1 rounded bg-gray-100 text-gray-700">
                            <span className="font-semibold mr-1">ROAS:</span> {ad.roas || 0}x
                          </div>
                          <div className="inline-flex items-center px-2 py-1 rounded bg-gray-100 text-gray-700">
                            <span className="font-semibold mr-1">Spend:</span> ${parseFloat(ad.spend || 0).toFixed(0)}
                          </div>
                          {ad.ctr && (
                            <div className="inline-flex items-center px-2 py-1 rounded bg-gray-100 text-gray-700">
                              <span className="font-semibold mr-1">CTR:</span> {parseFloat(ad.ctr || 0).toFixed(2)}%
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-12">
                  <div className="text-gray-400 text-6xl mb-4">📦</div>
                  <p className="text-gray-500 text-lg">No ads found</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Time Series Performance Chart View */}
      {viewMode === 'time-trend' && !loading && urlClusters && urlClusters.clusters && urlClusters.clusters.length > 0 && (() => {
        const groups = urlClusters.clusters.map(cluster => ({
          name: cluster.name,
          // cluster.ads contains raw ad IDs (strings), not objects
          adIds: (cluster.ads || []).map(ad => typeof ad === 'string' ? ad : (ad.ad_id || ad.raw_ad_id)).filter(Boolean)
        })).filter(group => group.adIds.length > 0);
        
        return groups.length > 0 && (
          <div className="mt-6">
            <GroupTimeSeriesChart 
              accountId={getAccountId()}
              groups={groups}
              dateRangeDays={filters?.dateRange || 7}
              numberOfPeriods={14}
              groupType="Product Cluster"
              startDate={filters?.dateRangeType === 'custom' ? filters?.startDate : null}
              endDate={filters?.dateRangeType === 'custom' ? filters?.endDate : null}
            />
          </div>
        );
      })()}
    </div>
  );
};

export default CreativeBreakdown;